import React, { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { captureException } from '@sentry/nextjs';
import { declOfNum } from '~/src/helpers/decline-of-num';
import {
    DEFAULT_FILE_SIZE_LIMIT_MB,
    DEFAULT_FORMAT,
    DEFAULT_TYPES_FILES_ARRAY,
    ERROR_FETCH,
    MAX_COUNT_TEXT,
    MIN_COUNT_FILES,
} from '~/src/components/common-components/dropzone-uploader/dropzone-uploader.constants';
import { useMatchMedia } from '~/src/hooks/use-match-media.hook';
import { MediaWidthEnum } from '~/src/models/enums/media-width.enum';
import {
    getAttachIcon,
    getFileDataSizeForAttach,
    getFileSizeLimitInBytes,
    getRulesText,
} from '~/src/components/common-components/dropzone-uploader/dropzone-uploader.utils';
import { DropZonePortalErrorMemo } from '~/src/components/common-components/dropzone-portal-error/dropzone-portal-error';
import { NO_AUTH_CODE } from '~/src/models/constants/status-codes';
import { AttachFile } from '~/src/components/common-components/attach-file/attach-file';
import type { AxiosResponse } from 'axios';
import type { Base64Type, ClickDropDownEvent, DropzoneProps } from './dropzone-uploader.types';
import { SvgDocumentDefault } from '~/src/components/svg-components/document-default';
import * as Styled from './dropzone-uploader.styles';

/**
 * Used in Module Federation
 */
const DropzoneUploader = <T, U>({
    ImageComponent = SvgDocumentDefault,
    children,
    typeField,
    className,
    maxCountFiles,
    titleText = 'Прикрепите один или несколько файлов',
    mainTextAlign = 'center',
    hideTitleAfterUpload,
    sectionName,
    getSizeAll,
    reset,
    progress,
    getCount,
    getFilesData,
    getIsSuccess,
    getFilesFromStore,
    resetDocument,
    postDocumentUpload,
    dropDocuments,
    setFileUploadStatus,
    deleteDocument,
    subtractSizeForField,
    setFileUploadData,
    sendButtonAnalytics,
    sendSuccessUploadFileAnalytics,
    withIcon,
    notificationPosition,
    portalRefCurrent,
    labelFormatFiles = DEFAULT_FORMAT,
    correctTypesFiles = DEFAULT_TYPES_FILES_ARRAY,
    maxSizeInMB = DEFAULT_FILE_SIZE_LIMIT_MB,
    required,
    fileErrorRef,
}: DropzoneProps<T, U>): JSX.Element => {
    const initStateError = { isError: false, showingError: false };
    const stateErrorTruth = { isError: true, showingError: true };
    const [isDragging, setIsDragging] = useState(false);
    const [isEntered, setIsEntered] = useState(false);
    const [isErrorFormatData, setIsErrorFormatData] = useState(initStateError);
    const [isErrorSizeData, setIsErrorSizeData] = useState(initStateError);
    const [isFetchErrorData, setIsFetchErrorData] = useState(initStateError);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [loadingDataFiles, setLoadingDataFiles] = useState<File[]>([]);
    const [errorDataFiles, setErrorDataFiles] = useState<File[]>([]);
    const [isErrorCountsData, setIsErrorCountsData] = useState(initStateError);
    const [titleIsHide, setTitleIsHide] = useState(false);
    const loadingDataFilesRef = useRef(loadingDataFiles);
    const dropZoneRef = useRef(null);

    const dropDownInnerRef = useRef<HTMLInputElement | null>(null);
    const [isMobile] = useMatchMedia(MediaWidthEnum.mobile, { withoutSSR: true });

    const countFiles = getCount(typeField);
    const declOfLimitedFiles = declOfNum(['файла', 'файлов', 'файлов']);
    const accept = correctTypesFiles?.join(', ');

    useEffect(() => {
        loadingDataFilesRef.current = loadingDataFiles;
    }, [loadingDataFiles.length]);

    useEffect(() => {
        !errorDataFiles.length && resetAllErrors();
    }, [errorDataFiles.length]);

    useEffect(() => {
        return () => {
            resetDocument(typeField);
            for (const elem of loadingDataFilesRef.current) {
                reset({ name: elem.name });
            }

            const blobs = getFilesFromStore(typeField);
            for (const blob of blobs) {
                window.URL.revokeObjectURL(blob.blobUrl);
            }
        };
    }, [children]);

    useEffect(() => {
        !loadingDataFiles.length && setIsLoadingFiles(false);
    }, [loadingDataFiles.length]);

    useEffect(() => {
        if (isFetchErrorData.isError && !loadingDataFiles.length) {
            setIsLoadingFiles(false);
        }
    }, [isFetchErrorData.isError, loadingDataFiles.length]);

    const onDragOverHandler = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const onMouseEnterHandler = () => {
        setIsEntered(true);
    };

    const onMouseLeaveHandler = () => {
        setIsEntered(false);
    };

    const onDragLeaveHandler = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const addFiles = (blobUrl: string, file: File, files: File[]) => {
        const { size, type, name } = file;
        dropDocuments({ blobUrl, name, type, typeField, size });
        setLoadingDataFiles((prev) => {
            return prev.filter((elem) => !files.includes(elem));
        });
    };

    const createFileInBase64 = async (file: File): Promise<Base64Type> => {
        const readerDataURL = new FileReader();
        readerDataURL.readAsDataURL(file);
        return await new Promise((resolve, reject) => {
            readerDataURL.onloadend = function (event) {
                resolve((event.target?.result || readerDataURL.result) as Base64Type);
            };
            readerDataURL.onerror = function () {
                reject('error');
                // eslint-disable-next-line no-console
                console.error('FileReader loaded error');
            };
        });
    };

    const createBlobURL = (blobFile: Blob) => {
        return window.URL.createObjectURL(blobFile);
    };

    const setTitleHide = (value: boolean) => {
        if (!hideTitleAfterUpload) {
            return;
        }

        setTitleIsHide(value);
    };

    const deleteFileHandler = (name: string) => {
        if (getIsSuccess(typeField)) {
            setFileUploadStatus({ isSuccess: false, typeField });
        }
        deleteDocument({ typeField, name });
        const [blob] = getFilesFromStore(typeField).filter((item) => item.name === name);
        subtractSizeForField({ typeField, size: blob.size });
        window.URL.revokeObjectURL(blob.blobUrl);
        setTitleHide(false);
    };

    const deleteLoadingFileHandler = async (name: string) => {
        reset({ name });
        setLoadingDataFiles((prev) => {
            return prev.filter((elem) => !(name === elem.name));
        });
        setTitleHide(false);
    };

    const deleteErrorFileHandler = (name: string) => {
        setErrorDataFiles((prev) => {
            return prev.filter((elem) => !(name === elem.name));
        });
        setTitleHide(false);
    };

    const createBlob = async (base64: Base64Type) => {
        return fetch(`${base64}`).then((res) => res.blob());
    };

    const startAddFilesProcess = (files: File[]) => {
        try {
            const promises = files.map(async (file) => {
                const fileInBase64 = await createFileInBase64(file);
                const blobFile = await createBlob(fileInBase64);
                const blobUrl = createBlobURL(blobFile);
                return { blobUrl, file };
            });
            Promise.all(promises).then((results) => {
                for (const { blobUrl, file } of results) {
                    addFiles(blobUrl, file, files);
                }
            });
        } catch (error) {
            setLoadingDataFiles((prev) => {
                return prev.filter((elem) => !files.includes(elem));
            });
            // eslint-disable-next-line no-console
            console.error('promises error (create docs)');
            captureException(error);
        }
    };

    const checkFormat = (files: File[], correctFiles: File[]) => {
        return files.length !== correctFiles.length;
    };

    const checkSize = (filesSize: number) => {
        const maxSizeLimit = getFileSizeLimitInBytes(maxSizeInMB);
        return filesSize > maxSizeLimit || getSizeAll(typeField) + filesSize > maxSizeLimit;
    };

    const checkIsSuccessInStore = (currentCounts: number) => {
        return countFiles === maxCountFiles || countFiles + currentCounts === maxCountFiles;
    };

    const checkErrorCounts = (currentCounts: number) => {
        return (
            (currentCounts > maxCountFiles || countFiles + currentCounts > maxCountFiles) && !getIsSuccess(typeField)
        );
    };

    const getFiles = (event: DragEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>): File[] => {
        if (event.type === 'change') {
            const target = event.target as HTMLInputElement;
            const files = target.files as FileList;
            return [...files];
        } else {
            const dragEvent = event as DragEvent;
            return [...dragEvent.dataTransfer.files];
        }
    };

    const saveDataFiles = (promises: Promise<{ data: Promise<AxiosResponse<U>>; name?: string }>[], files: File[]) => {
        let _files = files;
        const errorCodeCanceled = 'ERR_CANCELED';

        const promisesArray = promises.map(async (promise, key) => {
            try {
                const result = await promise;
                const resultData = await result.data;
                setFileUploadData({ typeField, data: { data: resultData?.data, name: result.name } });
                return files[key];
            } catch (error) {
                if (axios.isAxiosError(error) && error?.response?.status === NO_AUTH_CODE) {
                    return;
                }

                setFileUploadStatus({ isSuccess: false, typeField });
                if (axios.isAxiosError(error) && error?.code !== errorCodeCanceled) {
                    setErrorDataFiles((prev) => [...prev, files[key]]);
                    setIsFetchErrorData(stateErrorTruth);
                    setLoadingDataFiles((prev) => {
                        return prev.filter((elem) => !(files[key].name === elem.name));
                    });

                    // eslint-disable-next-line no-console
                    console.log('usePostDocumentUpload', error);
                    captureException(error);
                }

                _files = _files.filter((_, id) => id !== key);
                return null;
            }
        });

        return Promise.all(promisesArray);
    };

    const generateDataFiles = async (files: File[] | []) => {
        const filesToUpload = files;
        const promises = filesToUpload.map(async (file) => {
            return await postDocumentUpload({ file, name: file.name });
        });
        return saveDataFiles(promises, files);
    };

    const resetAllErrors = () => {
        const setError = (prev: typeof initStateError) => ({ ...prev, isError: false });
        setIsFetchErrorData(setError);
        setIsErrorFormatData(setError);
        setIsErrorSizeData(setError);
        setIsErrorCountsData(setError);
    };

    const onUploadHandler = async (event: DragEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setIsDragging(false);

        if (getIsSuccess(typeField)) {
            return;
        }

        !errorDataFiles.length && resetAllErrors();
        const defaultSizeValue = 0;
        const files = getFiles(event);
        const stateFilesNames = new Set(getFilesFromStore(typeField).map((elem) => elem.name));
        const loadingDataFilesNames = new Set(loadingDataFiles.map((elem) => elem.name));
        const errorDataFilesNames = new Set(errorDataFiles.map((elem) => elem.name));
        const filesUnique = files.filter(
            (elem) =>
                !stateFilesNames.has(elem.name) &&
                !loadingDataFilesNames.has(elem.name) &&
                !errorDataFilesNames.has(elem.name),
        );

        if (!filesUnique.length) {
            return;
        }
        const correctFiles = filesUnique.filter((elem) => correctTypesFiles?.includes(elem.type));
        const filesSize = filesUnique.reduce((acc: number, currentValue) => acc + currentValue.size, defaultSizeValue);

        if (checkFormat(filesUnique, correctFiles) && !getIsSuccess(typeField)) {
            setIsErrorFormatData(stateErrorTruth);
            return;
        }

        if (checkSize(filesSize) && !getIsSuccess(typeField)) {
            setIsErrorSizeData(stateErrorTruth);
            return;
        }

        if (checkErrorCounts(filesUnique.length)) {
            setIsErrorCountsData(stateErrorTruth);
            return;
        }

        if (checkIsSuccessInStore(filesUnique.length) && !getIsSuccess(typeField)) {
            setFileUploadStatus({ isSuccess: true, typeField });
        }

        setIsLoadingFiles(true);
        setLoadingDataFiles((prev) => [...prev, ...filesUnique]);
        setTitleHide(true);

        const newFiles = await generateDataFiles(filesUnique);
        const newFilesPure = newFiles.filter(Boolean);
        if (newFilesPure.length) {
            startAddFilesProcess(newFilesPure as File[]);
        }
    };

    const onClearValueHandler = (event: ClickDropDownEvent<HTMLInputElement>) => {
        if (event) {
            event.target.value = '';
        }
    };

    const getStatusAllFilesIsUploaded = () => {
        if (!countFiles || !getFilesData(typeField).length || isLoadingFiles) {
            return false;
        }
        return countFiles === getFilesData(typeField).length;
    };

    const getHasFreeSlotsForFiles = () => {
        return !(
            loadingDataFiles.length === maxCountFiles ||
            countFiles + loadingDataFiles.length === maxCountFiles ||
            errorDataFiles.length === maxCountFiles ||
            countFiles + errorDataFiles.length === maxCountFiles ||
            countFiles + errorDataFiles.length + loadingDataFiles.length === maxCountFiles
        );
    };

    const checkAllErrors = () => {
        return (
            isErrorCountsData.isError ||
            isErrorSizeData.isError ||
            isErrorFormatData.isError ||
            isFetchErrorData.isError
        );
    };

    const onClickChooseButton = () => {
        sendButtonAnalytics && sendButtonAnalytics(sectionName, children);
    };

    useEffect(() => {
        getIsSuccess(typeField) &&
            getStatusAllFilesIsUploaded() &&
            sendSuccessUploadFileAnalytics &&
            sendSuccessUploadFileAnalytics(sectionName, children);
    }, [getIsSuccess(typeField), getStatusAllFilesIsUploaded()]);

    const getIsSuccessField = () => {
        return (getIsSuccess(typeField) && getStatusAllFilesIsUploaded()) || !getHasFreeSlotsForFiles();
    };

    return (
        <Styled.DropZoneWrapper
            ref={dropZoneRef}
            className={className}
            needCentering={maxCountFiles === MIN_COUNT_FILES && getIsSuccessField()}
            titleIsHide={titleIsHide}
        >
            <Styled.TitleText>
                {!titleIsHide && titleText}
                {required && <Styled.RequiredStar>&#42;</Styled.RequiredStar>}
            </Styled.TitleText>
            {!(maxCountFiles === MIN_COUNT_FILES && getIsSuccessField()) && (
                <Styled.DropZoneHideWrapper>
                    <Styled.DropZoneContainer
                        isDragging={isDragging}
                        onMouseEnter={onMouseEnterHandler}
                        onMouseLeave={onMouseLeaveHandler}
                        withIcon={withIcon}
                    >
                        {withIcon && (
                            <Styled.IconContainer>
                                <Styled.ImageWrapper isDragging={isDragging}>
                                    <ImageComponent />
                                </Styled.ImageWrapper>
                                <div>
                                    <Styled.Title>{children}</Styled.Title>
                                </div>
                            </Styled.IconContainer>
                        )}
                        <Styled.MainTextContainer mainTextAlign={mainTextAlign}>
                            {countFiles !== maxCountFiles && getHasFreeSlotsForFiles() && (
                                <Styled.ControlText>
                                    <Styled.ControlTextInteractive
                                        onClick={onClickChooseButton}
                                        htmlFor={`input_${typeField}`}
                                    >
                                        Выберите файлы{' '}
                                    </Styled.ControlTextInteractive>
                                    {isMobile ? '' : '\nили переместите их сюда'}
                                </Styled.ControlText>
                            )}
                            {getIsSuccessField() && <Styled.MaxCountText>{MAX_COUNT_TEXT}</Styled.MaxCountText>}
                            <Styled.HiddenInput
                                onChange={onUploadHandler}
                                id={`input_${typeField}`}
                                ref={dropDownInnerRef}
                                type={'file'}
                                accept={accept}
                                onClick={onClearValueHandler}
                                multiple={maxCountFiles > MIN_COUNT_FILES}
                            />
                        </Styled.MainTextContainer>
                        <Styled.DraggableArea
                            onDragLeave={onDragLeaveHandler}
                            onDragOver={onDragOverHandler}
                            onDrop={onUploadHandler}
                            isEntered={isEntered}
                        />
                    </Styled.DropZoneContainer>
                    {!getIsSuccess(typeField) && required && (
                        <Styled.ErrorHiddenInput
                            ref={fileErrorRef}
                            role={'alert'}
                            name={'error-' + typeField}
                            defaultValue={'error'}
                        />
                    )}
                    <Styled.RulesText isError={checkAllErrors()}>
                        {getRulesText(maxSizeInMB, labelFormatFiles, declOfLimitedFiles(maxCountFiles), maxCountFiles)}
                    </Styled.RulesText>
                </Styled.DropZoneHideWrapper>
            )}
            {isLoadingFiles &&
                loadingDataFiles.map(({ name, size, type }, key) => (
                    <AttachFile
                        key={name + key + size}
                        ImageComponent={getAttachIcon(type)}
                        fileName={name}
                        isLoading={true}
                        progress={progress}
                        onCloseClick={() => deleteLoadingFileHandler(name)}
                    />
                ))}
            {errorDataFiles.map(({ name, size }, key) => (
                <AttachFile
                    key={name + key + size}
                    fileName={name}
                    textError={ERROR_FETCH}
                    onCloseClick={() => deleteErrorFileHandler(name)}
                />
            ))}
            {getFilesFromStore(typeField).map(({ name, size, type }, key) => (
                <AttachFile
                    ImageComponent={getAttachIcon(type)}
                    key={name + key + size}
                    fileName={name}
                    fileSize={getFileDataSizeForAttach(size).size}
                    unitsText={getFileDataSizeForAttach(size).units}
                    onCloseClick={() => deleteFileHandler(name)}
                />
            ))}
            <DropZonePortalErrorMemo
                isErrorFormatShow={isErrorFormatData.showingError}
                setIsErrorFormatData={setIsErrorFormatData}
                isFetchErrorShow={isFetchErrorData.showingError}
                setIsFetchErrorData={setIsFetchErrorData}
                maxSizeInMB={maxSizeInMB}
                setIsErrorCountsData={setIsErrorCountsData}
                limitedFiles={declOfLimitedFiles(maxCountFiles)}
                isErrorCountsShow={isErrorCountsData.showingError}
                portalRefCurrent={portalRefCurrent || dropZoneRef.current}
                maxCountFiles={maxCountFiles}
                notificationPosition={notificationPosition}
                setIsErrorSizeData={setIsErrorSizeData}
                isErrorSizeShow={isErrorSizeData.showingError}
            />
        </Styled.DropZoneWrapper>
    );
};

export default DropzoneUploader;
